import React from 'react'

import CTABookADemo from '../components/ctas/CTABookADemo'
import CTAEbook from '../components/ctas/CTAEbook'
import CTAInstantTour from '../components/ctas/CTAInstantTour'
import CTANewsletter from '../components/ctas/CTANewsletter'
import CTAProductWalkthrough from '../components/ctas/CTAProductWalkthrough'
import CTATemplates from '../components/ctas/CTATemplates'
import { EducationResourcesIdReferences } from '../constants/contentfulReferences'

// for now the incoming slug is from blog / learn category
// the return string is the slug of the respective educationResource from contentful that we want to present
export const getCtaEbookUrl = (slug: string) => {
  switch (slug) {
    case 'prioritization': // guide & blog category & glossary category & learn category
      return 'prioritization'
    case 'product-management': // guide & blog category & glossary category & learn category
      return 'product-management'
    case 'product-manager-job': // guide
      return 'modern-product-manager'
    case 'agile': // guide & blog category & glossary category
      return 'agile'
    case 'customer-feedback': // guide
    case 'feedback-management': // blog category & glossary category
    case 'discovery-and-feedback-management': // learn category
      return 'customer-feedback'
    case 'roadmaps': // guide
    case 'roadmapping': // blog category & glossary category & learn category
      return 'roadmapping'
    case 'internal-pm': // guide
      return 'internal-pm'
    case 'feature-factory': // guide
      return 'feature-factory'
    // case 'objectives-okrs': // guide
    //   return 'objectives-okrs'
    case 'product-operations': // guide
      return 'product-ops'
    case 'product-strategy': // blog category & glossary category & learn category
      return 'product-strategy'
    default:
      return 'product-management'
  }
}

export const getCtaEbookLpUrl = (isSidebarCta: boolean, categoryId: string, resourceSlug: string) => {
  switch (categoryId) {
    // educationCategory id is used here for:
    // eBooks
    case EducationResourcesIdReferences.eBooks:
      return `/cta/${isSidebarCta ? 'sidebar' : 'inline'}/resources/ebooks/${resourceSlug}/`
    // 5-minute guides
    case EducationResourcesIdReferences.fiveMinGuides:
      return `/cta/${isSidebarCta ? 'sidebar' : 'inline'}/resources/5-min-guides/${resourceSlug}/`
    // Checklists
    case EducationResourcesIdReferences.checklists:
      return `/cta/${isSidebarCta ? 'sidebar' : 'inline'}/resources/checklists/${resourceSlug}/`
    // Reports - DISABLED FOR NOW (2025-02-13)
    // case EducationResourcesIdReferences.reports:
    //   return `/cta/${isSidebarCta ? 'sidebar' : 'inline'}/resources/reports/${resourceSlug}/`
    default:
      return `/cta/${isSidebarCta ? 'sidebar' : 'inline'}/resources/${resourceSlug}/`
  }
}

export const getCtaTemplatesCategoryUrl = (slug: string) => {
  switch (slug) {
    case 'feedback-management': // blog category
    case 'customer-feedback': // guide
    case 'discovery-and-feedback-management': // learn category
      return 'feedback-management'
    case 'prioritization': // blog category & learn category
      return 'prioritization'
    case 'product-management': // blog category & learn category
      return 'product-management'
    case 'roadmapping': // blog category & learn category
    case 'roadmaps': // guide
      return 'roadmaps'
    default:
      return 'product-management'
  }
}

export const getCtaInstantTourType = (slug: string) => {
  switch (slug) {
    case 'roadmaps': // guide
    case 'roadmapping': // blog category & learn category
      return 'roadmaps'
    case 'prioritization': // blog category & learn category & guide
      return 'prioritization'
    case 'objectives-okrs': // guide
    case 'product-strategy': // blog category & learn category & guide
      return 'objectives-okrs'
    default:
      // default value will be provided by useInstantTourLinkFactory()
      return undefined
  }
}

export const getCtaSidebar = (funnelStage: 'TOFU' | 'MOFU' | 'BOFU', categorySlug: string) => {
  switch (funnelStage) {
    case 'TOFU':
      return <CTAEbook isSidebarCta educationResourceUrl={getCtaEbookUrl(categorySlug)} />
    case 'MOFU':
      return <CTATemplates templatesCategoryUrl={getCtaTemplatesCategoryUrl(categorySlug)} isSidebarCta />
    case 'BOFU':
      return <CTABookADemo isSidebarCta />
    default:
      return <CTAEbook isSidebarCta educationResourceUrl={getCtaEbookUrl(categorySlug)} />
  }
}

export const transformCrossSellingEntryToCtaInline = (
  type:
    | 'TOFU - Main'
    | 'TOFU - Additional' // for now (2023-01-01) this is exactly the same as TOFU - Main
    | 'MOFU - Main'
    | 'MOFU - Additional'
    | 'BOFU - Main'
    | 'BOFU - Additional'
    | 'Book a demo'
    | 'eBook / Report'
    | 'Newsletter'
    | 'Product Walkthrough'
    | 'Templates',
  resourceSlug: string,
  funnelStage: 'TOFU' | 'MOFU' | 'BOFU',
  categorySlug: string,
  title: string
) => {
  // most recent setup: type of CTA is selected within RichText (TOFU - BOFU acc. marketing spreadsheet / logic)
  if (type) {
    switch (type) {
      case 'TOFU - Main':
        return <CTAEbook educationResourceUrl={getCtaEbookUrl(categorySlug)} />
      case 'TOFU - Additional':
        // for now (2023-01-01) this is exactly the same as TOFU - Main
        return <CTAEbook educationResourceUrl={getCtaEbookUrl(categorySlug)} />
      case 'MOFU - Main':
        return <CTATemplates templatesCategoryUrl={getCtaTemplatesCategoryUrl(categorySlug)} />
      case 'MOFU - Additional':
        return <CTANewsletter />
      case 'BOFU - Main':
        return <CTABookADemo />
      case 'BOFU - Additional':
        // 2023-09-04: deactivation of ProductWalkthroughCTA - exchange with CTAInstantTour
        // return <CTAProductWalkthrough />
        return <CTAInstantTour instantTourType={getCtaInstantTourType(categorySlug)} />
      case 'Book a demo':
        return <CTABookADemo />
      case 'eBook / Report':
        return <CTAEbook educationResourceUrl={resourceSlug} />
      case 'Newsletter':
        return <CTANewsletter />
      case 'Product Walkthrough':
        return <CTAProductWalkthrough />
      case 'Templates':
        return <CTATemplates templatesCategoryUrl={getCtaTemplatesCategoryUrl(categorySlug)} />
      default:
        return <CTAEbook />
    }
  }
  // if at least funnelStage is provided on Post / Article: return CTA acc. marketing spreadsheet / logic
  if (funnelStage) {
    switch (funnelStage) {
      case 'TOFU':
        return <CTAEbook educationResourceUrl={getCtaEbookUrl(categorySlug)} />
      case 'MOFU':
        return <CTATemplates templatesCategoryUrl={getCtaTemplatesCategoryUrl(categorySlug)} />
      case 'BOFU':
        return <CTABookADemo />
    }
  }
  // else: transform the old standard Cross Selling Entry depending on its title
  switch (title) {
    case 'Get our OKRs Roundtable Report':
      return <CTAEbook educationResourceUrl="roundtable-debate-okrs" />
    case 'Get our Customer Feedback eBook':
      return <CTAEbook educationResourceUrl="customer-feedback" />
    case 'Get our Mastering Prioritization eBook':
      return <CTAEbook educationResourceUrl="prioritization" />
    case 'Get our How To Succeed as a Modern Product Manager eBook':
      return <CTAEbook educationResourceUrl="modern-product-manager" />
    case 'Get our Agile: Best Practices and Methodologies eBook':
      return <CTAEbook educationResourceUrl="agile" />
    case 'Get our Customer Feedback eBook':
      return <CTAEbook educationResourceUrl="customer-feedback" />
    case 'Get our Roadmapping From A to Z eBook':
      return <CTAEbook educationResourceUrl="roadmapping" />
    case 'Get our Internal Product Management eBook':
      return <CTAEbook educationResourceUrl="internal-pm" />
    case 'Get our OKRs Roundtable Report':
      return <CTAEbook educationResourceUrl="roundtable-debate-okrs" />
    case 'Learn from product experts':
      return <CTAProductWalkthrough />
    default:
      return <CTAEbook />
  }
}

export const getCtaMainType = (slug: string) => {
  switch (slug) {
    case 'Customer Feedback': // event category name
    case 'feedback-management': // blog category & templates category
    case 'discovery-and-feedback-management': // learn category
      return 'insights'
    case 'Prioritization': // event category name
    case 'prioritization': // blog category & learn category & templates category
      return 'prioritization'
    case 'Product Strategy': // event category name
    case 'business': // templates category
    case 'product-discovery': // blog category
    case 'product-strategy': // blog category & learn category
    case 'releases': // templates category
      return 'product-strategy'
    case 'Roadmapping': // event category name
    case 'roadmapping': // blog category & learn category
    case 'roadmaps': // templates category
      return 'roadmaps'
    default:
      return 'general'
  }
}
